import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ThemeToggle } from "../components/ui/ThemeToggle";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";

import { useAuth } from "../context/AuthContext";
import { useIsMobileViewport } from "../hooks/useIsMobileViewport";

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  background:
    "radial-gradient(circle at top left, rgba(198,165,106,0.14), transparent 30%), radial-gradient(circle at bottom right, rgba(201,107,99,0.08), transparent 24%), var(--background)",
  color: "var(--text-primary)",
  transition: "background var(--transition-base), color var(--transition-base)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "var(--page-padding)",
  position: "relative",
  overflowX: "hidden",
};

const mobilePageStyle: React.CSSProperties = {
  ...pageStyle,
  alignItems: "flex-start",
  padding: "88px var(--mobile-page-padding) 32px",
};

const toggleWrapperStyle: React.CSSProperties = {
  position: "absolute",
  top: "24px",
  right: "24px",
  zIndex: 10,
};

const mobileToggleWrapperStyle: React.CSSProperties = {
  ...toggleWrapperStyle,
  top: "20px",
  right: "20px",
};

const gridStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "1180px",
  display: "grid",
  gridTemplateColumns: "1.08fr 0.92fr",
  gap: "28px",
  alignItems: "stretch",
};

const heroCardStyle: React.CSSProperties = {
  padding: "40px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  minHeight: "600px",
};

const formCardStyle: React.CSSProperties = {
  padding: "32px",
  width: "100%",
  maxWidth: "460px",
  marginLeft: "auto",
  minHeight: "600px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

const mobileFormCardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "var(--content-max-width-sm)",
  margin: "0 auto",
};

const brandRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "14px",
};

const mobileBrandRowStyle: React.CSSProperties = {
  ...brandRowStyle,
  marginBottom: "28px",
};

const logoBoxStyle: React.CSSProperties = {
  width: "52px",
  height: "52px",
  borderRadius: "var(--radius-md)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--primary-soft)",
  border: "1px solid var(--border)",
  color: "var(--primary)",
  fontWeight: 800,
  fontSize: "18px",
  letterSpacing: "0.08em",
  flexShrink: 0,
};

const brandMetaStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
};

const brandEyebrowStyle: React.CSSProperties = {
  fontSize: "12px",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--text-secondary)",
};

const brandNameStyle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const titleStyle: React.CSSProperties = {
  margin: "28px 0 14px 0",
  fontSize: "clamp(38px, 5vw, 62px)",
  lineHeight: 0.98,
  letterSpacing: "-0.04em",
  fontWeight: 800,
};

const subtitleStyle: React.CSSProperties = {
  margin: 0,
  maxWidth: "560px",
  color: "var(--text-secondary)",
  fontSize: "16px",
  lineHeight: 1.65,
};

const infoGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "14px",
  marginTop: "28px",
};

const infoCardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.03)",
  padding: "18px",
};

const formHeaderTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "30px",
  fontWeight: 800,
  letterSpacing: "-0.02em",
};

const mobileFormHeaderTitleStyle: React.CSSProperties = {
  ...formHeaderTitleStyle,
  fontSize: "28px",
};

const formHeaderTextStyle: React.CSSProperties = {
  marginTop: "10px",
  marginBottom: "28px",
  color: "var(--text-secondary)",
  lineHeight: 1.6,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "14px",
  fontWeight: 600,
  marginBottom: "8px",
  color: "var(--text-primary)",
};

const fieldWrapperStyle: React.CSSProperties = {
  marginBottom: "18px",
};

const passwordFieldStyle: React.CSSProperties = {
  position: "relative",
};

const passwordToggleStyle: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  right: "14px",
  transform: "translateY(-50%)",
  border: "none",
  background: "transparent",
  color: "var(--text-secondary)",
  cursor: "pointer",
  padding: "4px",
  fontSize: "18px",
  lineHeight: 1,
  zIndex: 2,
};

const helperStyle: React.CSSProperties = {
  marginTop: "6px",
  marginBottom: "20px",
  color: "var(--text-secondary)",
  fontSize: "13px",
};

const footerTextStyle: React.CSSProperties = {
  marginTop: "18px",
  textAlign: "center",
  color: "var(--text-secondary)",
  fontSize: "13px",
  lineHeight: 1.6,
};

const errorStyle: React.CSSProperties = {
  marginBottom: "18px",
  padding: "12px 14px",
  borderRadius: "14px",
  border: "1px solid rgba(201,107,99,0.24)",
  background: "rgba(201,107,99,0.10)",
  color: "var(--text-primary)",
  fontSize: "14px",
};

type LoginResponse = {
  token?: string;
  user?: {
    id?: number | string;
    rol?: string;
    username?: string;
    first_name?: string;
    last_name?: string;
  };
  error?: string;
};

const Login = () => {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const navigate = useNavigate();
  const { login, isAuthenticated, rol } = useAuth();
  const isMobile = useIsMobileViewport();

  useEffect(() => {
    if (!isAuthenticated || !rol) {
      return;
    }

    const roleUpper = rol.toUpperCase();

    if (roleUpper === "ADMIN") {
      navigate("/admin", { replace: true });
      return;
    }

    if (roleUpper === "DRIVER") {
      navigate("/driver", { replace: true });
      return;
    }

    if (roleUpper === "SELLER") {
      navigate("/seller", { replace: true });
      return;
    }

    console.log("Rol no reconocido:", rol);
  }, [isAuthenticated, rol, navigate]);

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/v2/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            username: username.trim(),
            password,
          }),
        },
      );

      const data: LoginResponse = await response.json();

      if (!response.ok || !data.token || !data.user) {
        throw new Error(data.error || "Login fallido");
      }

      login({
        token: data.token,
        user: {
          id: data.user.id ?? null,
          username: data.user.username ?? "",
          rol: (data.user.rol || "").toUpperCase(),
          first_name: data.user.first_name ?? "",
          last_name: data.user.last_name ?? "",
        },
      });
    } catch (error) {
      console.error(error);
      setErrorMessage("Usuario o contraseña incorrectos.");
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const renderPasswordField = () => (
    <div style={fieldWrapperStyle}>
      <label style={labelStyle} htmlFor="password">
        Contraseña
      </label>

      <div style={passwordFieldStyle}>
        <Input
          id="password"
          type={showPassword ? "text" : "password"}
          placeholder="Ingresá tu contraseña"
          value={password}
          onChange={handlePasswordChange}
        />

        <button
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
          aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
          title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
          style={passwordToggleStyle}
        >
          {showPassword ? "🙈" : "👁️"}
        </button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div style={mobilePageStyle}>
        <div style={mobileToggleWrapperStyle}>
          <ThemeToggle />
        </div>

        <div style={mobileFormCardStyle}>
          <Card padding="24px">
            <div style={mobileBrandRowStyle}>
              <div style={logoBoxStyle}>LX</div>

              <div style={brandMetaStyle}>
                <span style={brandEyebrowStyle}>
                  Plataforma de gestión logística
                </span>
                <span style={brandNameStyle}>Lynx</span>
              </div>
            </div>

            <h1 style={mobileFormHeaderTitleStyle}>Iniciar sesión</h1>

            <p style={formHeaderTextStyle}>
              Ingresá tus credenciales para continuar.
            </p>

            <form onSubmit={handleLogin}>
              {errorMessage ? (
                <div style={errorStyle}>{errorMessage}</div>
              ) : null}

              <div style={fieldWrapperStyle}>
                <label style={labelStyle} htmlFor="username">
                  Usuario
                </label>

                <Input
                  id="username"
                  type="text"
                  placeholder="Ingresá tu usuario"
                  value={username}
                  onChange={handleUsernameChange}
                />
              </div>

              {renderPasswordField()}

              <div style={helperStyle}>
                Acceso para administración, sellers y choferes.
              </div>

              <Button type="submit" fullWidth disabled={loading}>
                {loading ? "Ingresando..." : "Ingresar"}
              </Button>
            </form>

            <div style={footerTextStyle}>Lynx Platform</div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={toggleWrapperStyle}>
        <ThemeToggle />
      </div>

      <div style={gridStyle}>
        <Card style={heroCardStyle}>
          <div>
            <div style={brandRowStyle}>
              <div style={logoBoxStyle}>LX</div>

              <div style={brandMetaStyle}>
                <span style={brandEyebrowStyle}>
                  Plataforma de gestión logística
                </span>
                <span style={brandNameStyle}>Lynx</span>
              </div>
            </div>

            <h1 style={titleStyle}>Acceso a la plataforma</h1>

            <p style={subtitleStyle}>
              Gestioná operación, envíos, usuarios y métricas desde un solo
              lugar.
            </p>
          </div>

          <div style={infoGridStyle}>
            <div style={infoCardStyle}>
              <div
                style={{
                  fontSize: "13px",
                  color: "var(--text-secondary)",
                  marginBottom: "8px",
                }}
              >
                Operación
              </div>
              <div style={{ fontSize: "22px", fontWeight: 800 }}>
                Centralizada
              </div>
            </div>

            <div style={infoCardStyle}>
              <div
                style={{
                  fontSize: "13px",
                  color: "var(--text-secondary)",
                  marginBottom: "8px",
                }}
              >
                Gestión
              </div>
              <div style={{ fontSize: "22px", fontWeight: 800 }}>
                En tiempo real
              </div>
            </div>

            <div style={infoCardStyle}>
              <div
                style={{
                  fontSize: "13px",
                  color: "var(--text-secondary)",
                  marginBottom: "8px",
                }}
              >
                Plataforma
              </div>
              <div style={{ fontSize: "22px", fontWeight: 800 }}>
                Escalable
              </div>
            </div>
          </div>
        </Card>

        <Card style={formCardStyle}>
          <div>
            <h2 style={formHeaderTitleStyle}>Iniciar sesión</h2>

            <p style={formHeaderTextStyle}>
              Ingresá tus credenciales para continuar.
            </p>

            <form onSubmit={handleLogin}>
              {errorMessage ? (
                <div style={errorStyle}>{errorMessage}</div>
              ) : null}

              <div style={fieldWrapperStyle}>
                <label style={labelStyle} htmlFor="username">
                  Usuario
                </label>

                <Input
                  id="username"
                  type="text"
                  placeholder="Ingresá tu usuario"
                  value={username}
                  onChange={handleUsernameChange}
                />
              </div>

              {renderPasswordField()}

              <div style={helperStyle}>
                Acceso para administración, sellers y choferes.
              </div>

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                style={{
                  transform: loading ? "scale(0.995)" : "scale(1)",
                }}
              >
                {loading ? "Ingresando..." : "Ingresar"}
              </Button>
            </form>

            <div style={footerTextStyle}>Lynx Platform</div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Login;